/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import {
  getAccountDataAssertionsDecoder,
  getAccountDataAssertionsEncoder,
  type AccountDataAssertions,
  type AccountDataAssertionsArgs,
} from '../../hooked';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getLogLevelDecoder,
  getLogLevelEncoder,
  type LogLevelArgs,
} from '../types';

export const ASSERT_ACCOUNT_DATA_MULTI_DISCRIMINATOR = 3;

export function getAssertAccountDataMultiDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_ACCOUNT_DATA_MULTI_DISCRIMINATOR);
}

export type AssertAccountDataMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertAccountDataMultiInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertions: AccountDataAssertions;
};

export type AssertAccountDataMultiInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertions: AccountDataAssertionsArgs;
};

export function getAssertAccountDataMultiInstructionDataEncoder(): Encoder<AssertAccountDataMultiInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertions', getAccountDataAssertionsEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_ACCOUNT_DATA_MULTI_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertAccountDataMultiInstructionDataDecoder(): Decoder<AssertAccountDataMultiInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertions', getAccountDataAssertionsDecoder()],
  ]);
}

export function getAssertAccountDataMultiInstructionDataCodec(): Codec<
  AssertAccountDataMultiInstructionDataArgs,
  AssertAccountDataMultiInstructionData
> {
  return combineCodec(
    getAssertAccountDataMultiInstructionDataEncoder(),
    getAssertAccountDataMultiInstructionDataDecoder()
  );
}

export type AssertAccountDataMultiInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertAccountDataMultiInstructionDataArgs['logLevel'];
  assertions: AssertAccountDataMultiInstructionDataArgs['assertions'];
};

export function getAssertAccountDataMultiInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertAccountDataMultiInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertAccountDataMultiInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertAccountDataMultiInstructionDataEncoder().encode(
      args as AssertAccountDataMultiInstructionDataArgs
    ),
  } as AssertAccountDataMultiInstruction<
    TProgramAddress,
    TAccountTargetAccount
  >;

  return instruction;
}

export type ParsedAssertAccountDataMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertAccountDataMultiInstructionData;
};

export function parseAssertAccountDataMultiInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertAccountDataMultiInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertAccountDataMultiInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
